/** Chat V2 sidebar — hierarchy from spacing, type, and hover, not item strokes. */

import { alpha } from '@mui/material/styles';

/** Shared inset so header, nav, and recent rows sit on one grid. */
export const CHAT_SIDEBAR_GUTTER_PX = 12;
export const CHAT_SIDEBAR_GUTTER_COLLAPSED_PX = 8;

export const CHAT_SIDEBAR_SECTION_LABEL_SX = {
  display: 'block',
  px: `${CHAT_SIDEBAR_GUTTER_PX}px`,
  pt: 0,
  pb: 0.75,
  fontSize: 10.5,
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  opacity: 0.72,
};

export const CHAT_SIDEBAR_HEADER_TITLE_SX = {
  fontSize: 13,
  fontWeight: 700,
  lineHeight: '18px',
  letterSpacing: 0,
  color: 'text.primary',
};

export const CHAT_SIDEBAR_HEADER_ROW_SX = {
  px: `${CHAT_SIDEBAR_GUTTER_PX}px`,
  pt: 1.25,
  pb: 1,
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.5,
};

export const CHAT_SIDEBAR_NAV_SECTION_SX = {
  px: `${CHAT_SIDEBAR_GUTTER_PX}px`,
  pt: 0.25,
  pb: 0.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
};

export const CHAT_SIDEBAR_DIVIDER_SX = {
  mx: `${CHAT_SIDEBAR_GUTTER_PX}px`,
  mt: 1.75,
  mb: 1.5,
  borderColor: 'divider',
};

export const CHAT_SIDEBAR_COLLAPSE_BUTTON_SX = {
  width: { xs: 40, md: 28 },
  height: { xs: 40, md: 28 },
  p: 0,
  ml: 'auto',
  flexShrink: 0,
  color: 'text.secondary',
  borderRadius: 0.75,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&:hover': {
    bgcolor: 'action.hover',
    color: 'text.primary',
  },
};

const navStartIconSx = {
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: 1,
    '& > *:nth-of-type(1)': {
      fontSize: 16,
    },
  },
};

/** Which sidebar nav item is selected. Only one at a time. */
export function getChatSidebarNavActive({ activeView = 'chat', selectedSessionId = null } = {}) {
  if (activeView === 'library') return 'library';
  if (selectedSessionId) return 'session';
  return 'new';
}

export function chatSidebarNavItemSx(theme, { active = false, collapsed = false } = {}) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : 'action.hover';
  const activeBg = alpha(primary, isDark ? 0.16 : 0.08);

  return {
    minHeight: 34,
    width: '100%',
    ...(collapsed ? { width: 32, height: 32, minWidth: 32, minHeight: 32, p: 0 } : {}),
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: active ? 600 : 500,
    fontSize: 13,
    lineHeight: '18px',
    px: collapsed ? 0 : 1.25,
    py: collapsed ? 0 : 0.5,
    boxShadow: 'none',
    border: 'none',
    bgcolor: active ? activeBg : 'transparent',
    color: active ? 'primary.main' : 'text.primary',
    ...navStartIconSx,
    '& .MuiButton-startIcon, & .MuiSvgIcon-root': {
      color: 'inherit',
    },
    '&:hover': {
      bgcolor: active ? alpha(primary, isDark ? 0.2 : 0.12) : hoverBg,
      border: 'none',
      boxShadow: 'none',
      color: active ? 'primary.main' : 'text.primary',
    },
    '&.Mui-disabled': {
      opacity: 0.55,
    },
  };
}

export function chatSidebarLibraryActionSx(theme, options = {}) {
  return chatSidebarNavItemSx(theme, options);
}

export function chatSidebarShellSx({ isTemporary = false } = {}) {
  return (theme) => {
    const isDark = theme.palette.mode === 'dark';
    return {
      position: 'relative',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: isDark ? '#181818' : 'background.paper',
      color: 'text.primary',
      borderTop: 0,
      borderRight: isTemporary ? 0 : '1px solid',
      borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(88, 89, 91, 0.16)',
      transition: 'width 180ms ease, flex-basis 180ms ease',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    };
  };
}

export function chatSidebarRecentItemSx(theme, { selected = false } = {}) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';

  return {
    minHeight: 36,
    py: 0.75,
    px: 1.25,
    mx: 0,
    borderRadius: 1,
    mb: 0.25,
    color: 'text.primary',
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: alpha(primary, isDark ? 0.12 : 0.06),
      color: 'text.primary',
    },
    '&:hover': {
      bgcolor: hoverBg,
    },
    '& .MuiListItemText-root': {
      margin: 0,
    },
    '& .MuiListItemText-primary': {
      fontSize: 12.5,
      fontWeight: selected ? 500 : 400,
      lineHeight: '18px',
      color: selected ? 'text.primary' : 'text.secondary',
    },
  };
}

